import type { CoreEntity } from '#/index'
import { CoreEntityType } from '#/index'
import { useNovelCoreStore } from '@/stores/novelCoreStore'
import { useThesisBriefStore } from '@/stores/thesisBriefStore'
import type { ThesisCloudPayload } from '@/lib/thesisCloudApi'

/** 收集一篇论文的云同步包：Brief + 相关实体 */
export function collectThesisCloudPayload(novelId: string): {
  title: string
  payload: ThesisCloudPayload
} {
  const state = useNovelCoreStore.getState()
  const novel = state.novels.find((n) => n.id === novelId)
  const title = novel?.attributes?.title || '未命名论文'

  const actIds = new Set((novel?.relationships?.acts || []) as string[])
  const acts = state.novelActs.filter(
    (a) => a.relationships?.project?.id === novelId || actIds.has(a.id),
  )
  for (const a of acts) actIds.add(a.id)

  const chapterIds = new Set<string>()
  for (const a of acts) {
    for (const id of (a.relationships?.chapters || []) as string[]) chapterIds.add(id)
  }
  const chapters = state.novelChapters.filter(
    (c) => c.relationships?.project?.id === novelId || chapterIds.has(c.id),
  )
  for (const c of chapters) chapterIds.add(c.id)

  const sceneIds = new Set<string>()
  for (const c of chapters) {
    for (const id of (c.relationships?.scenes || []) as string[]) sceneIds.add(id)
  }
  const scenes = state.novelScenes.filter(
    (s) => s.relationships?.project?.id === novelId || sceneIds.has(s.id),
  )

  const entities: CoreEntity[] = []
  if (novel) entities.push(novel as CoreEntity)
  entities.push(...(acts as CoreEntity[]), ...(chapters as CoreEntity[]), ...(scenes as CoreEntity[]))

  const brief = useThesisBriefStore.getState().ensureBrief(novelId)

  return {
    title,
    payload: {
      schemaVersion: 1,
      brief,
      entities,
    },
  }
}

/** 把云端包写回本机 IndexedDB + store + Brief */
export async function applyThesisCloudPayload(
  novelId: string,
  payload: ThesisCloudPayload,
): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!payload || payload.schemaVersion !== 1) {
    return { ok: false, error: '不支持的云端包版本' }
  }
  const store = useNovelCoreStore.getState()
  const entities = Array.isArray(payload.entities) ? (payload.entities as CoreEntity[]) : []

  for (const ent of entities) {
    if (!ent?.id || !ent?.type) continue
    const existing = store.getCoreDataById(ent.id)
    if (existing) {
      const r = await store.updateCoreData(ent as any)
      if (r?.code !== 200) {
        // 尝试直接 add 覆盖路径
        await store.addCoreData(ent)
      }
    } else {
      await store.addCoreData(ent)
    }
  }

  if (payload.brief && typeof payload.brief === 'object') {
    const brief = { ...(payload.brief as object), novelId } as Parameters<
      ReturnType<typeof useThesisBriefStore.getState>['ensureBrief']
    >[1]
    useThesisBriefStore.getState().ensureBrief(novelId, brief as any)
  }

  await store.initNovelDetailsById(novelId)
  return { ok: true }
}

export function isNovelEntityType(t: string): boolean {
  return (
    t === CoreEntityType.Novels ||
    t === CoreEntityType.NovelActs ||
    t === CoreEntityType.NovelChapters ||
    t === CoreEntityType.NovelScenes
  )
}
